import {
  OktonCheckbox,
  OktonCollapseCard,
  OktonConfirmDialog,
  OktonLoadingState,
  OktonSelect,
  OktonTextarea,
} from '@okton/platform-ui';
import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';

import SecaoDocumentosPedido from '../../components/SecaoDocumentosPedido';
import Page from '../../layout/IntegraAppPage';
import {
  formatarData,
  formatarValor,
  obterNomeCanal,
  usePedidoDetalhado,
} from '../../services/hooks/usePedidoDetalhado';

type RouteParams = {
  id: string;
};

type InfoProps = {
  label: string;
  value: React.ReactNode;
};

const STATUS_POS_VENDA = [
  { value: 1, label: 'Novo' },
  { value: 2, label: 'Em separação' },
  { value: 3, label: 'Separado' },
  { value: 4, label: 'Enviado' },
  { value: 5, label: 'Problema' },
  { value: 6, label: 'Finalizado' },
];

const Info: React.FC<InfoProps> = ({ label, value }) => {
  const corLabel = useColorModeValue('gray.600', 'gray.400');
  const corValor = useColorModeValue('gray.800', 'whiteAlpha.900');

  return (
    <Box minW={0}>
      <Text fontSize="xs" color={corLabel} mb={1}>
        {label}
      </Text>

      <Text fontSize="sm" fontWeight="600" color={corValor} noOfLines={2}>
        {value || '-'}
      </Text>
    </Box>
  );
};

const DetalhesPedido: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();

  const cores = {
    fundoPagina: useColorModeValue('gray.50', 'gray.900'),
    fundoCard: useColorModeValue('white', 'gray.800'),
    corBorda: useColorModeValue('gray.200', 'gray.700'),
    fundoItem: useColorModeValue('gray.50', 'gray.700'),
  };

  const corTextoPagina = useColorModeValue('gray.800', 'white');
  const corSubtitulo = useColorModeValue('gray.500', 'gray.400');
  const corTextoSecundario = useColorModeValue('gray.600', 'gray.400');

  const {
    pedido,
    dados,
    estaCarregando,
    estaSalvandoPosVenda,
    estaCancelandoPedido,
    salvarPosVenda,
    cancelarPedido,
  } = usePedidoDetalhado(id);

  const [statusPosVenda, setStatusPosVenda] = useState('');
  const [observacaoInterna, setObservacaoInterna] = useState('');
  const [conferido, setConferido] = useState(false);
  const [confirmarCancelamento, setConfirmarCancelamento] = useState(false);

  useEffect(() => {
    if (!pedido) return;

    setStatusPosVenda(
      pedido.status_pos_venda ? String(pedido.status_pos_venda) : '',
    );
    setObservacaoInterna(pedido.observacao_interna || '');
    setConferido(Boolean(pedido.conferido));
  }, [pedido]);

  const salvarDadosPosVenda = async () => {
    await salvarPosVenda({
      status_pos_venda: statusPosVenda ? Number(statusPosVenda) : null,
      observacao_interna: observacaoInterna.trim() || null,
      conferido,
    });
  };

  if (estaCarregando) {
    return (
      <Page>
        <OktonLoadingState title="Carregando pedido..." minH="60vh" />
      </Page>
    );
  }

  if (!pedido) {
    return (
      <Page>
        <Flex align="center" justify="center" minH="60vh">
          <Stack align="center" spacing={4}>
            <Text color={corTextoSecundario}>Pedido não encontrado.</Text>

            <Button
              leftIcon={<FiArrowLeft />}
              onClick={() => history.push('/pedidos')}
            >
              Voltar para pedidos
            </Button>
          </Stack>
        </Flex>
      </Page>
    );
  }

  const cancelarPedidoMercadoLivre = async () => {
    await cancelarPedido({
      reason: 'OTHER_MY_RESPONSIBILITY',
      message: 'Cancelamento solicitado pelo painel.',
      restock_item: true,
    });
    setConfirmarCancelamento(false);
  };

  const itens = pedido.itens || [];

  const podeCancelarPedido =
    pedido.marketplace === 'mercadolivre' &&
    Number(pedido.status) !== 4 &&
    Number(pedido.status_envio) !== 203 &&
    Number(pedido.status_envio) !== 204;

  return (
    <Page>
      <Box bg={cores.fundoPagina} minH="100vh" py={6} color={corTextoPagina}>
        <Container maxW="1600px">
          <Flex align="center" justify="space-between" mb={6} gap={4}>
            <Flex align="center" gap={4}>
              <IconButton
                aria-label="Voltar"
                icon={<FiArrowLeft />}
                variant="ghost"
                borderRadius="full"
                onClick={() => history.goBack()}
              />

              <VStack align="start" spacing={0}>
                <Heading size="md">
                  Pedido #{pedido.marketplace_order_id}
                </Heading>

                <Text fontSize="xs" color={corSubtitulo}>
                  Pedido interno #{pedido.id}
                </Text>
              </VStack>
            </Flex>

            <HStack spacing={3}>
              {podeCancelarPedido && (
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="sm"
                  isLoading={estaCancelandoPedido}
                  onClick={() => setConfirmarCancelamento(true)}
                >
                  Cancelar pedido
                </Button>
              )}

              <Badge
                colorScheme={pedido.status_cor || 'gray'}
                variant="subtle"
                px={3}
                py={1}
                borderRadius="md"
              >
                {pedido.status_label || '-'}
              </Badge>
            </HStack>
          </Flex>

          <Stack spacing={6}>
            <OktonCollapseCard
              title="Itens vendidos"
              subtitle={`${itens.length} item(ns) neste pedido`}
              defaultOpen
            >
              <Stack spacing={3}>
                {itens.map(item => (
                  <Box
                    key={item.id}
                    bg={cores.fundoItem}
                    borderWidth="1px"
                    borderColor={cores.corBorda}
                    borderRadius="md"
                    p={4}
                  >
                    <Flex
                      justify="space-between"
                      gap={4}
                      direction={{ base: 'column', md: 'row' }}
                    >
                      <Box minW={0}>
                        <Text fontWeight="bold" noOfLines={2}>
                          {item.titulo}
                        </Text>

                        <Text fontSize="xs" color={corTextoSecundario}>
                          Item Mercado Livre: {item.marketplace_item_id}
                        </Text>

                        <Text fontSize="xs" color={corTextoSecundario}>
                          Produto #{item.produto_id || '-'} | Anúncio #
                          {item.anuncio_id || '-'}
                        </Text>
                      </Box>

                      <HStack spacing={8} align="flex-start">
                        <Info label="Qtd." value={String(item.quantidade)} />
                        <Info
                          label="Unitário"
                          value={formatarValor(item.preco_unitario)}
                        />
                        <Info label="Total" value={formatarValor(item.total)} />
                      </HStack>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            </OktonCollapseCard>

            <OktonCollapseCard
              title="Resumo"
              subtitle="Informações principais do pedido"
              defaultOpen
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                <Info
                  label="Canal"
                  value={obterNomeCanal(pedido.marketplace)}
                />
                <Info label="Total" value={formatarValor(pedido.total)} />
                <Info
                  label="Itens"
                  value={String(dados.totalItens || itens.length)}
                />
                <Info
                  label="Estoque"
                  value={
                    <Badge
                      colorScheme={
                        pedido.estoque_estornado
                          ? 'purple'
                          : pedido.estoque_processado
                          ? 'green'
                          : 'orange'
                      }
                      variant="subtle"
                    >
                      {pedido.estoque_estornado
                        ? 'Estornado'
                        : pedido.estoque_processado
                        ? 'Processado'
                        : 'Pendente'}
                    </Badge>
                  }
                />
                <Info
                  label="Status do pedido"
                  value={pedido.status_label || '-'}
                />
                <Info
                  label="Status do pagamento"
                  value={pedido.status_pagamento_label || '-'}
                />
                <Info
                  label="Status do envio"
                  value={pedido.status_envio_label || '-'}
                />
                <Info
                  label="Data do pedido"
                  value={formatarData(pedido.data_pedido)}
                />
                <Info
                  label="Data de aprovação"
                  value={formatarData(pedido.data_aprovacao)}
                />
                <Info
                  label="Última sincronização"
                  value={formatarData(pedido.sincronizado_em)}
                />
              </SimpleGrid>
            </OktonCollapseCard>

            <OktonCollapseCard
              title="Cliente"
              subtitle="Dados do comprador vinculados ao pedido"
              defaultOpen
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                <Info label="Nome" value={dados.comprador.nome} />
                <Info label="Email" value={dados.comprador.email} />
                <Info label="Documento" value={dados.comprador.documento} />
                <Info
                  label="ID do comprador"
                  value={String(dados.comprador.id)}
                />
                <Info
                  label="Apelido no Mercado Livre"
                  value={dados.comprador.nickname}
                />
                <Info
                  label="ID dos dados fiscais"
                  value={String(dados.comprador.billingInfoId)}
                />
                <Info label="Empresa" value={`#${pedido.empresa_id}`} />
                <Info label="Filial" value={`#${pedido.filial_id}`} />
              </SimpleGrid>
            </OktonCollapseCard>

            <OktonCollapseCard
              title="Pagamento"
              subtitle="Status, método e valores recebidos do marketplace"
              defaultOpen={false}
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                <Info
                  label="ID do pagamento"
                  value={String(dados.pagamento.id)}
                />
                <Info label="Status" value={dados.pagamento.status} />
                <Info label="Detalhe" value={dados.pagamento.detalhe} />
                <Info label="Método" value={dados.pagamento.metodo} />
                <Info label="Tipo" value={dados.pagamento.tipo} />
                <Info label="Valor pago" value={dados.pagamento.valorPago} />
                <Info
                  label="Valor estornado"
                  value={dados.pagamento.valorEstornado}
                />
              </SimpleGrid>
            </OktonCollapseCard>

            <OktonCollapseCard
              title="Envio"
              subtitle="Informações de entrega e vendedor"
              defaultOpen={false}
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                <Info label="ID do envio" value={String(dados.envio.id)} />
                <Info label="Status do envio" value={dados.envio.status} />
                <Info
                  label="Código de rastreio"
                  value={dados.envio.codigoRastreio}
                />
                <Info
                  label="Transportadora"
                  value={dados.envio.transportadora}
                />
                <Info
                  label="Data de envio"
                  value={formatarData(dados.envio.dataEnvio)}
                />
                <Info
                  label="Previsão de entrega"
                  value={formatarData(dados.envio.dataEntregaPrevista)}
                />
                <Info
                  label="Data de entrega"
                  value={formatarData(dados.envio.dataEntrega)}
                />
                <Info
                  label="Rastreio"
                  value={
                    dados.envio.urlRastreio ? (
                      <Button
                        as="a"
                        href={dados.envio.urlRastreio}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        variant="outline"
                      >
                        Abrir rastreio
                      </Button>
                    ) : (
                      '-'
                    )
                  }
                />
                <Info
                  label="ID do vendedor"
                  value={String(dados.envio.vendedorId)}
                />
                <Info
                  label="Apelido do vendedor"
                  value={dados.envio.vendedorNickname}
                />
              </SimpleGrid>
            </OktonCollapseCard>

            <SecaoDocumentosPedido pedidoId={pedido.id} />

            <OktonCollapseCard
              title="Dados fiscais"
              subtitle="Dados usados para identificação fiscal do comprador"
              defaultOpen={false}
            >
              {dados.fiscal.disponivel ? (
                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                  <Info label="Nome fiscal" value={dados.fiscal.nome} />
                  <Info label="Sobrenome" value={dados.fiscal.sobrenome} />
                  <Info label="Documento" value={dados.fiscal.documento} />
                  <Info
                    label="Tipo documento"
                    value={dados.fiscal.documentoTipo}
                  />
                  <Info label="Endereço" value={dados.fiscal.endereco || '-'} />
                  <Info label="Complemento" value={dados.fiscal.complemento} />
                  <Info label="Cidade" value={dados.fiscal.cidade} />
                  <Info label="Bairro" value={dados.fiscal.bairro} />
                  <Info label="Estado" value={dados.fiscal.estado} />
                  <Info label="CEP" value={dados.fiscal.cep} />
                  <Info label="País" value={dados.fiscal.pais} />
                </SimpleGrid>
              ) : (
                <Text color={corTextoSecundario}>
                  {dados.fiscal.erro ||
                    'Dados fiscais indisponíveis para este pedido.'}
                </Text>
              )}
            </OktonCollapseCard>

            <OktonCollapseCard
              title="Pós-venda"
              subtitle="Status interno, conferência e observações"
              defaultOpen
            >
              <Stack spacing={5}>
                <Flex align="center" gap={3} flexWrap="wrap">
                  <Text fontSize="xs" color={corTextoSecundario}>
                    Status atual
                  </Text>

                  <Badge
                    colorScheme={pedido.status_pos_venda_cor || 'gray'}
                    variant="subtle"
                    px={3}
                    py={1}
                    borderRadius="md"
                  >
                    {pedido.status_pos_venda_label || '-'}
                  </Badge>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                  <OktonSelect
                    name="statusPosVenda"
                    label="Status do pós-venda"
                    size="sm"
                    value={statusPosVenda}
                    onChange={event => setStatusPosVenda(event.target.value)}
                    options={[
                      { value: '', label: 'Selecione' },
                      ...STATUS_POS_VENDA.map(status => ({
                        value: status.value,
                        label: status.label,
                      })),
                    ]}
                  />

                  <Flex align="end">
                    <OktonCheckbox
                      name="conferido"
                      label="Pedido conferido"
                      isChecked={conferido}
                      onChange={event => setConferido(event.target.checked)}
                    />
                  </Flex>
                </SimpleGrid>

                <OktonTextarea
                  name="observacaoInterna"
                  label="Observação interna"
                  value={observacaoInterna}
                  onChange={event => setObservacaoInterna(event.target.value)}
                  placeholder="Adicione uma observação interna sobre o atendimento do pedido"
                  resize="vertical"
                  minH="110px"
                />

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
                  <Info
                    label="Conferido em"
                    value={formatarData(pedido.conferido_em)}
                  />
                  <Info
                    label="Finalizado em"
                    value={formatarData(pedido.finalizado_em)}
                  />
                  <Info
                    label="Última atualização"
                    value={formatarData(pedido.atendimento_atualizado_em)}
                  />
                </SimpleGrid>

                <Flex justify="flex-end">
                  <Button
                    colorScheme="blue"
                    size="sm"
                    isLoading={estaSalvandoPosVenda}
                    onClick={salvarDadosPosVenda}
                  >
                    Salvar pós-venda
                  </Button>
                </Flex>
              </Stack>
            </OktonCollapseCard>
          </Stack>
        </Container>
      </Box>
      <OktonConfirmDialog
        isOpen={confirmarCancelamento}
        onClose={() => setConfirmarCancelamento(false)}
        onConfirm={cancelarPedidoMercadoLivre}
        title="Cancelar pedido"
        description="Tem certeza que deseja cancelar este pedido no Mercado Livre?"
        confirmLabel="Cancelar pedido"
        cancelLabel="Voltar"
        intent="danger"
      />
    </Page>
  );
};

export default DetalhesPedido;
